import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { useParams } from "react-router-dom";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import TextArea from "@/common/components/TextArea";
import { ShowHeader } from "@/common/components/ShowHeader";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FormTransformers } from "@/common/utils/formTransformers";
import { UpdateTmaisRafflesValidationSchema, updateSchema } from "../../schemas/tmaisRaffles.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import SelectController from "@/common/components/SelectController";
import { UnitTextField } from "@/common/components/UnitTextField";
import { DateInput } from "@/common/components/DateInput/DateInput";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import useTmaisRaffle from "../../hooks/useTmaisRaffle";
import useUpdateTmaisRaffle from "../../hooks/useUpdateTmaisRaffle";
import { winnerOperationKlassOptions } from "../../constants/winnerOperationKlassOptions.constant";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { MultipleImageUploader } from "@/common/components/MultipleImageUploader";
import MaskedTextField from "@/common/components/MaskedTextField";
import { timeMask } from "@/common/constants/masks.constant";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import { formatDate, formatTime } from "@/common/utils/formatters";

const Update = () => {
  const { id } = useParams();
  const goBack = useGoBack();
  const [messages, setMessages] = useState<string[] | boolean>(false)
  const queryClient = useQueryClient();

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs();

  const {
    data: initialRaffle,
    isPending: initialRaffleIsPending,
    isFetched: initialRaffleIsFetched
  } = useTmaisRaffle(id || '');

  const {
    mutate,
    isPending: updateRaffleIsPending
  } = useUpdateTmaisRaffle({
    id: id || '',
    initialTmaisRaffle: initialRaffle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tmaisRaffle', id] });
      goBack({ fallback: `/tmais-raffles/${id}` })
    },
    onError: (error: any) => {
      if (error?.response?.data?.errors.base) {
        setMessages(error.response?.data?.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setMessages, setError })(error)
      }
    }
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToUpdateTmaisRaffle =
    isUserPermissionsPending || hasUserPermission('Establishment::Tmais::Raffles::Update')

  const onSubmit: SubmitHandler<UpdateTmaisRafflesValidationSchema> = (data) => mutate(data);

  const formattedPointsPerTickets = initialRaffle?.points_per_ticket?.toLocaleString()
  const formattedNumberOfTickets = initialRaffle?.number_of_tickets?.toLocaleString()
  const maxTicketsPerUser = initialRaffle?.max_tickets_per_user?.toString()

  const availableFromDate = initialRaffle?.available_from ? formatDate(new Date(initialRaffle.available_from)) : undefined
  const availableUntilDate = initialRaffle?.available_until ? formatDate(new Date(initialRaffle.available_until)) : undefined

  const winnerDateObject = initialRaffle?.select_winner_at ? new Date(initialRaffle.select_winner_at) : null
  const formattedWinnerDate = winnerDateObject ? formatDate(winnerDateObject) : undefined
  const formattedWinnerTime = winnerDateObject ? formatTime(winnerDateObject, false) : undefined

  const {
    register,
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors }
  } = useForm<UpdateTmaisRafflesValidationSchema>({
    resolver: zodResolver(updateSchema),
    defaultValues: {
      display_name: initialRaffle?.display_name,
      description: initialRaffle?.description,
      available_from: availableFromDate,
      available_until: availableUntilDate,
      select_winner_at: formattedWinnerDate,
      select_winner_time_at: formattedWinnerTime,
      max_tickets_per_user: maxTicketsPerUser,
    }
  });

  useEffect(() => {
    reset({
      pictures_attachments_attributes: initialRaffle?.pictures?.map((photo) => ({ id: photo.id, file: photo?.url })) || [],
      select_winner_operation_klass: winnerOperationKlassOptions.find((option) => option.value === initialRaffle?.select_winner_operation_klass)
    })
  }, [initialRaffleIsFetched])

  return (
    <form
      className="flex flex-col h-[calc(100vh-64px)]"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="false"
    >
      <ShowHeader
        goBack
        goBackProps={{ fallback: `/tmais-raffles` }}
        title="Sorteios"
        subtitle={initialRaffle?.display_name}
        buttons={userHasPermissionToUpdateTmaisRaffle ? (
          <Button
            type="submit"
            className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
          >
            Salvar
          </Button>
        ) : undefined}
      />

      {initialRaffleIsPending ? (
        <Box>Carregando...</Box>
      ) : (
        <ErrorMessageWrapper
          className="max-h-[calc(100vh-128px)]"
          statusCode={userHasPermissionToUpdateTmaisRaffle ? 200 : 403}
        >
          <Box className="flex flex-col flex-1 overflow-auto gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
            <h2 className="text-lg font-bold">Detalhes do Sorteio</h2>
            <h3 className="text-base font-bold">Nome e imagens do sorteio</h3>

            <div className="flex gap-4">
              <MultipleImageUploader
                label="Anexe uma foto"
                name="pictures_attachments_attributes"
                accept="image/jpeg, image/png"
                uploadBlob={uploadBlob}
                containerClassName="max-w-[288px]"
                control={control}
                fieldName="pictures_attachments_attributes"
                disabled={uploadBlobIsPending || updateRaffleIsPending}
                errorMessage={errors.pictures_attachments_attributes?.message || undefined}
                uploadButtonLayout="floating"
              />
              <div className="flex flex-col gap-4 w-full">
                <TextField
                  label="Nome"
                  {...register('display_name')}
                  errorMessage={errors?.display_name?.message}
                  defaultValue={initialRaffle?.display_name}
                  markAsRequired
                  className="w-full"
                />

                <TextArea
                  label="Descrição"
                  markAsRequired
                  errorMessage={errors?.description?.message}
                  defaultValue={initialRaffle?.description}
                  {...register('description')}
                  rows={8}
                />
              </div>
            </div>

            <h2 className="text-base font-bold">Configurações do sorteio</h2>

            <div className="flex gap-2">
              <TextField
                label="Custo em pontos do bilhete"
                className="w-1/3"
                value={formattedPointsPerTickets}
                disabled
              />
              <TextField
                label="Quantidade de bilhetes"
                className="w-1/3"
                value={formattedNumberOfTickets}
                disabled
              />
              <UnitTextField
                label="Máximo de bilhetes por usuário"
                {...register('max_tickets_per_user')}
                errorMessage={errors?.max_tickets_per_user?.message}
                disabled={updateRaffleIsPending}
                defaultValue={initialRaffle?.max_tickets_per_user}
                markAsRequired
                decimals={0}
                className="w-1/3"
              />
            </div>

            <div className="flex gap-2">
              <SelectController
                name="select_winner_operation_klass"
                control={control}
                label="Forma do sorteio"
                options={winnerOperationKlassOptions}
                errorMessage={errors?.select_winner_operation_klass?.message}
                isDisabled={updateRaffleIsPending}
                markAsRequired
                className="w-1/2"
              />
              <div className="flex flex-col w-1/2 gap-2">
                <div className="flex gap-2">
                  <DateInput
                    fieldName="select_winner_at"
                    control={control}
                    errors={errors}
                    label="Dia do sorteio"
                    markAsRequired
                    disabled={updateRaffleIsPending}
                    className="w-1/2"
                  />
                  <Controller
                    name="select_winner_time_at"
                    control={control}
                    render={({ field: { onChange, value, name } }) => (
                      <MaskedTextField
                        mask={timeMask}
                        label="Hora do sorteio"
                        placeholder="00:00"
                        name={name}
                        value={value || ""}
                        onChange={onChange}
                        className="w-1/2"
                      />
                    )}
                  />
                </div>
                {errors.select_winner_at?.message && <ErrorMessage name='select_winner_at' message={errors.select_winner_at.message} />}
              </div>
            </div>

            <div className="flex gap-2">
              <DateInput
                fieldName="available_from"
                control={control}
                errors={errors}
                errorMessage={errors.available_from?.message || undefined}
                label="Data inicial para participação"
                markAsRequired
                disabled={updateRaffleIsPending}
                className="w-1/2"
              />
              <DateInput
                fieldName="available_until"
                control={control}
                errors={errors}
                errorMessage={errors.available_until?.message || undefined}
                label="Data final para participação"
                disabled={updateRaffleIsPending}
                className="w-1/2"
              />
            </div>
          </Box>
        </ErrorMessageWrapper>
      )}

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  );
};

export default Update;
